import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Grid,
  TextField,
  Button,
  Avatar,
  Alert,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  CircularProgress,
  Snackbar,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  InputAdornment
} from '@mui/material';
import {
  Percent as PercentIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Add as AddIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Savings as SavingsIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  ReceiptLong as ReceiptIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { format } from 'date-fns';

// Ionic Imports
import { IonContent, IonPage } from '@ionic/react';
import { 
  createCommission, 
  getCommission, 
  updateCommission 
} from '../../../api/analytics/analytics';

// Types
interface Commission {
  commission_id: string;
  commission_percent: number;
  status: string;
  created_at: string;
  updated_at: string;
}

interface CommissionHistory {
  id: number;
  commission_id: string;
  commission_percent: number;
  status: string;
  created_at: string;
  updated_at: string;
}

// Main Component
const CommissionManager = () => {
  const theme = useTheme();
  const history = useHistory();

  // Data states
  const [currentCommission, setCurrentCommission] = useState<Commission | null>(null);
  const [commissionHistory, setCommissionHistory] = useState<CommissionHistory[]>([]);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  
  // Form states
  const [newCommissionPercent, setNewCommissionPercent] = useState<number>(0);
  const [editCommissionPercent, setEditCommissionPercent] = useState<number>(0);
  const [editCommissionId, setEditCommissionId] = useState<string>('');
  
  // UI states
  const [loading, setLoading] = useState({
    fetch: false,
    create: false,
    update: false
  });
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch current commission on component mount
  useEffect(() => {
    fetchCurrentCommission();
  }, []);

  const fetchCurrentCommission = async () => {
    setLoading(prev => ({ ...prev, fetch: true }));
    try {
      const response = await getCommission();
      const data = response?.data?.data || response?.data;
      
      if (data && Object.keys(data).length > 0) {
        setCurrentCommission(data);
      } else {
        setCurrentCommission(null);
      }
      setError(null);
    } catch (err: any) {
      console.error('Error fetching commission:', err);
      if (err?.response?.data?.message === "No commission found") {
        setCurrentCommission(null);
      } else {
        setError("Failed to load commission settings");
        showNotification("Failed to load commission settings", 'error');
      }
    } finally {
      setLoading(prev => ({ ...prev, fetch: false }));
    }
  };

  const handleCreateCommission = async () => {
    if (!newCommissionPercent || newCommissionPercent <= 0) {
      showNotification('Please enter a valid commission percentage', 'warning');
      return;
    }

    if (newCommissionPercent > 100) {
      showNotification('Commission percentage cannot exceed 100%', 'warning');
      return;
    }

    setLoading(prev => ({ ...prev, create: true }));
    try {
      await createCommission(newCommissionPercent);
      showNotification('Commission created successfully', 'success');
      setCreateDialogOpen(false);
      setNewCommissionPercent(0);
      await fetchCurrentCommission();
    } catch (err: any) {
      console.error('Error creating commission:', err);
      showNotification(err?.response?.data?.message || 'Failed to create commission', 'error');
    } finally {
      setLoading(prev => ({ ...prev, create: false }));
    }
  };

  const handleUpdateCommission = async () => {
    if (!editCommissionPercent || editCommissionPercent <= 0) {
      showNotification('Please enter a valid commission percentage', 'warning');
      return;
    }

    if (editCommissionPercent > 100) {
      showNotification('Commission percentage cannot exceed 100%', 'warning');
      return;
    }

    setLoading(prev => ({ ...prev, update: true }));
    try {
      await updateCommission(editCommissionId, editCommissionPercent);
      showNotification('Commission updated successfully', 'success');
      setEditDialogOpen(false);
      setEditCommissionPercent(0);
      setEditCommissionId('');
      await fetchCurrentCommission();
    } catch (err: any) {
      console.error('Error updating commission:', err);
      showNotification(err?.response?.data?.message || 'Failed to update commission', 'error');
    } finally {
      setLoading(prev => ({ ...prev, update: false }));
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchCurrentCommission();
    setRefreshing(false);
    showNotification('Data refreshed successfully', 'success');
  };

  const openEditDialog = () => {
    if (currentCommission) {
      setEditCommissionId(currentCommission.commission_id);
      setEditCommissionPercent(currentCommission.commission_percent);
      setEditDialogOpen(true);
    }
  };

  const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm:ss');
    } catch {
      return 'Invalid Date';
    }
  };

  const formatPercent = (percent: number) => {
    return `${percent}%`;
  };

  return (
    <IonPage>
      <IonContent>
        <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
          
          {/* Header */}
          <Paper 
            elevation={0}
            sx={{ 
              borderBottom: `1px solid ${theme.palette.divider}`,
              position: 'sticky',
              top: 0,
              zIndex: 10,
              bgcolor: 'background.paper'
            }}
          >
            <Container maxWidth="lg">
              <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 2 }}>
                <IconButton onClick={() => history.goBack()} edge="start">
                  <BackIcon />
                </IconButton>
                <Avatar
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main
                  }}
                >
                  <PercentIcon />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h5" fontWeight={600}>
                    Commission Manager
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage platform commission rates
                  </Typography>
                </Box>
                <Tooltip title="Refresh">
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={handleRefresh}
                    disabled={refreshing}
                    size="small"
                  >
                    Refresh
                  </Button>
                </Tooltip>
              </Stack>
            </Container>
          </Paper>

          <Container maxWidth="lg" sx={{ py: 3 }}>
            
            {/* Current Commission Card */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Card sx={{ 
                  mb: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.dark, 0.9)} 100%)`,
                  color: 'white'
                }}>
                  <CardContent sx={{ p: 4 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack spacing={2}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <PercentIcon sx={{ fontSize: 32 }} />
                          <Typography variant="h6" sx={{ opacity: 0.9 }}>
                            Current Commission Rate
                          </Typography>
                        </Stack>
                        
                        {loading.fetch ? (
                          <CircularProgress size={48} sx={{ color: 'white' }} />
                        ) : currentCommission ? (
                          <>
                            <Typography variant="h1" sx={{ fontWeight: 700, fontSize: { xs: 48, md: 64 } }}>
                              {formatPercent(currentCommission.commission_percent)}
                            </Typography>
                            <Stack direction="row" spacing={2}>
                              <Chip 
                                label={`Last updated: ${formatDate(currentCommission.updated_at)}`}
                                size="small"
                                sx={{ bgcolor: alpha('#fff', 0.2), color: 'white' }}
                              />
                              <Chip 
                                label={currentCommission.status}
                                size="small"
                                sx={{ bgcolor: alpha('#4caf50', 0.8), color: 'white' }}
                              />
                            </Stack>
                          </>
                        ) : (
                          <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            No Commission Set
                          </Typography>
                        )}
                      </Stack>
                      
                      <Stack spacing={2}>
                        {currentCommission ? (
                          <Button
                            variant="contained"
                            startIcon={<EditIcon />}
                            onClick={openEditDialog}
                            sx={{
                              bgcolor: 'white',
                              color: theme.palette.primary.main,
                              '&:hover': {
                                bgcolor: alpha('#fff', 0.9)
                              }
                            }}
                          >
                            Update Commission
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setCreateDialogOpen(true)}
                            sx={{
                              bgcolor: 'white',
                              color: theme.palette.primary.main,
                              '&:hover': {
                                bgcolor: alpha('#fff', 0.9)
                              }
                            }}
                          >
                            Set Commission
                          </Button>
                        )}
                        
                        <Button
                          variant="outlined"
                          startIcon={<HistoryIcon />}
                          onClick={() => setHistoryDialogOpen(true)}
                          sx={{
                            borderColor: 'white',
                            color: 'white',
                            '&:hover': {
                              borderColor: 'white',
                              bgcolor: alpha('#fff', 0.1)
                            }
                          }}
                        >
                          View History
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Info Cards */}
              <Grid item xs={12} md={4}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: theme.palette.success.main }}>
                            <SavingsIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Platform Revenue
                            </Typography>
                            <Typography variant="h6" fontWeight={600}>
                              {currentCommission ? `${currentCommission.commission_percent}% of transactions` : 'Not set'}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main }}>
                            <TrendingUpIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              Impact on Vendors
                            </Typography>
                            <Typography variant="body2">
                              {currentCommission 
                                ? `Vendors receive ${100 - currentCommission.commission_percent}% of transaction value`
                                : 'Set commission to calculate vendor earnings'}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Information Section */}
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                About Commission Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <MoneyIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Transaction Commission
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Commission is calculated as a percentage of each successful transaction
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <ReceiptIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Automatic Deduction
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Commission is automatically deducted from vendor payouts
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <HistoryIcon color="primary" />
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Rate Changes
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        All commission rate changes are logged for audit purposes
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>

          </Container>
        </Box>

        {/* Create Commission Dialog */}
        <Dialog 
          open={createDialogOpen} 
          onClose={() => !loading.create && setCreateDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <PercentIcon color="primary" />
              <Typography variant="h6">Set Commission Rate</Typography>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                This commission will be applied to all future transactions. The rate cannot exceed 100%.
              </Alert>
              
              <FormControl fullWidth>
                <TextField
                  autoFocus
                  label="Commission Percentage (%)"
                  type="number"
                  value={newCommissionPercent}
                  onChange={(e) => setNewCommissionPercent(parseFloat(e.target.value))}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  helperText="Enter a value between 0 and 100"
                  fullWidth
                />
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setCreateDialogOpen(false)}
              disabled={loading.create}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateCommission}
              variant="contained"
              disabled={loading.create || !newCommissionPercent || newCommissionPercent <= 0}
              startIcon={loading.create ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            >
              {loading.create ? 'Creating...' : 'Create Commission'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Update Commission Dialog */}
        <Dialog 
          open={editDialogOpen} 
          onClose={() => !loading.update && setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <EditIcon color="primary" />
              <Typography variant="h6">Update Commission Rate</Typography>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Alert severity="warning" sx={{ mb: 3 }}>
                Changing the commission rate will affect all future transactions.
              </Alert>
              
              <FormControl fullWidth>
                <TextField
                  autoFocus
                  label="Current Commission"
                  type="number"
                  value={editCommissionPercent}
                  onChange={(e) => setEditCommissionPercent(parseFloat(e.target.value))}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                  }}
                  helperText="Enter a value between 0 and 100"
                  fullWidth
                />
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setEditDialogOpen(false)}
              disabled={loading.update}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateCommission}
              variant="contained"
              color="primary"
              disabled={loading.update || !editCommissionPercent || editCommissionPercent <= 0}
              startIcon={loading.update ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            >
              {loading.update ? 'Updating...' : 'Update Commission'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* History Dialog */}
        <Dialog 
          open={historyDialogOpen} 
          onClose={() => setHistoryDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" spacing={1}>
              <HistoryIcon color="primary" />
              <Typography variant="h6">Commission History</Typography>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Commission ID</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created At</TableCell>
                    <TableCell>Last Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentCommission ? (
                    <TableRow hover>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {currentCommission.commission_id}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={formatPercent(currentCommission.commission_percent)}
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={currentCommission.status}
                          size="small"
                          color={currentCommission.status === 'active' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(currentCommission.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(currentCommission.updated_at)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography color="text.secondary" sx={{ py: 4 }}>
                          No commission history available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHistoryDialogOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Refresh Indicator */}
        {refreshing && (
          <Box
            sx={{
              position: 'fixed',
              top: 80,
              right: 24,
              zIndex: 1100
            }}
          >
            <Paper sx={{ p: 1, bgcolor: alpha(theme.palette.primary.main, 0.9), color: 'white' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} color="inherit" />
                <Typography variant="caption">Refreshing...</Typography>
              </Stack>
            </Paper>
          </Box>
        )}

      </IonContent>
    </IonPage>
  );
};

export default CommissionManager;